import { queryOptions } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { fetchIssue, isIssueMissing } from '../../api/issueRead.api';
import { latestForIssue } from '../../domain/feed';
import type { FeedItem, IssueHolding } from '../../types/domain.types';
import { CTL_GC, CTL_STALE, ctlKeys } from '../../utils/query-keys';
import { myFeedQueryOptions } from '../../utils/queryOptions';

export const issuesKey = () => [...ctlKeys.all, 'issue'] as const;

export const issueKey = (issueId: string) => [...issuesKey(), issueId] as const;

const readIssueOrMissing = async (issueId: string, signal: AbortSignal): Promise<FeedItem | null> => {
  try {
    return await fetchIssue(issueId, signal);
  } catch (error) {
    if (isIssueMissing(error)) return null;
    throw error;
  }
};

const feedRowFor = (queryClient: QueryClient, issueId: string): FeedItem | undefined => {
  const page = queryClient.getQueryData(myFeedQueryOptions().queryKey);
  return page === undefined ? undefined : latestForIssue(page.items, issueId) ?? undefined;
};

export const issueQueryOptions = (queryClient: QueryClient, issueId: string) =>
  queryOptions<FeedItem | null, Error, FeedItem | null, ReturnType<typeof issueKey>>({
    queryKey: issueKey(issueId),
    queryFn: ({ signal }) => readIssueOrMissing(issueId, signal),
    placeholderData: () => feedRowFor(queryClient, issueId),
    staleTime: CTL_STALE.feed,
    gcTime: CTL_GC,
  });

export const holdIssue = async (queryClient: QueryClient, issueId: string, holding: IssueHolding): Promise<void> => {
  const queryKey = issueKey(issueId);
  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData<FeedItem | null>(queryKey, (item) =>
    item === undefined || item === null
      ? item
      : { ...item, startedBy: holding.startedBy ?? item.startedBy, status: holding.status ?? item.status, nextAction: holding.nextAction },
  );
};

export const refreshIssue = (queryClient: QueryClient, issueId: string): void => {
  void queryClient.invalidateQueries({ queryKey: issueKey(issueId) });
};
